import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { UserSettings, getDefaultUserSettings, getUserSettingsOrDefault } from "../models/UserSettings";
import { LOCAL_STORAGE_THEME_KEY } from "../models/Common";

export const sessionIdStorageKey = "materiahunter-sessionId";

export type UserState = {
    showLoading: boolean,
    showLogin: boolean,
    isAuthInitialized: boolean,
    isAdmin: boolean,
    isContributor: boolean,
    isLoggedIn: boolean,
    loggedInUserId: string,
    dirtyVariants: Record<string, number>,
    isCollectionLoading: boolean,
    collectionDownloadProgress: number,
    collection: Record<string, number>,
    sessionId: string,
    userSettings: UserSettings,
    userSettingsFetched: boolean
}

const initialState: UserState = {
    showLoading: true,
    showLogin: false,
    isAuthInitialized: false,
    isAdmin: false,
    isContributor: false,
    isLoggedIn: false,
    loggedInUserId: "",
    dirtyVariants: {},

    isCollectionLoading: false,
    collectionDownloadProgress: 0,
    collection: {},
    sessionId: "",
    userSettings: getDefaultUserSettings(),
    userSettingsFetched: false
};

export const userSlice = createSlice({
    name: 'user',
    initialState: initialState,
    reducers: {
        setUserSettings: (state, action: PayloadAction<UserSettings>) => {
            state.userSettings = getUserSettingsOrDefault(action.payload);
            state.userSettingsFetched = true;

            const html = document.querySelector("html");
            html?.setAttribute("data-theme", action.payload.isDarkMode ? "dark" : "light");

            localStorage.setItem(LOCAL_STORAGE_THEME_KEY, `${action.payload.isDarkMode}`);
        },
        setIsLoggedIn: (state, action: PayloadAction<{
            isLoggedIn: boolean,
            isAdmin: boolean,
            isContributor: boolean,
            loggedInUserId: string
        }>) => {
            state.showLoading = false;
            state.showLogin = !action.payload.isLoggedIn;
            state.isAuthInitialized = true;
            state.isLoggedIn = action.payload.isLoggedIn;
            state.loggedInUserId = action.payload.loggedInUserId;
            state.isAdmin = action.payload.isAdmin;
            state.isContributor = action.payload.isContributor;
        },
        setCollection: (state, action: PayloadAction<Record<string, number>>) => {
            state.collection = action.payload;
        },
        setDirtyVariants: (state, action: PayloadAction<Record<string, number>>) => {
            state.dirtyVariants = action.payload;
        },

        setIsCollectionLoading: (state, action: PayloadAction<boolean>) => {
            state.isCollectionLoading = action.payload;
        },
        setCollectionDownloadProgress: (state, action: PayloadAction<number>) => {
            state.collectionDownloadProgress = action.payload * 100;
        },
        setSession: (state, action: PayloadAction<{ sessionId: string }>) => {
            const storedSessionId = localStorage.getItem(sessionIdStorageKey);

            if (storedSessionId) {
                state.sessionId = storedSessionId;
            } else {
                state.sessionId = action.payload.sessionId;
                localStorage.setItem(sessionIdStorageKey, action.payload.sessionId);
            }
        }
    }
});

export const {
    setIsLoggedIn,
    setDirtyVariants,

    setCollectionDownloadProgress,
    setIsCollectionLoading,
    setCollection,
    setSession,
    setUserSettings,
} = userSlice.actions;

export default userSlice.reducer;